import { cn } from "@gymflow/helpers";
import { FormPhoneNumberInput } from "apps/portal/src/components/atoms/form/text/FormPhoneNumberInput";
import { FormikProvider, useFormik } from "formik";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { queryClient } from "libs/api/src/lib/apiV2/QueryClient";
import { useMutationCreateAccount } from "libs/api/src/lib/hooks/memberAsPublic/useMutationCreateAccount";
import { SignUpSchema } from "libs/validation/src/SignUpSchema";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import {
  Button,
  FormCheckboxInput,
  FormInput,
} from "../../../components/atoms";
import { FormPasswordInput } from "../../../components/atoms/form/text/FormPasswordInput";
import { usePortalRoutes } from "../../../hooks";
import useGymflowModels from "../../../store";
import { OnboardingContext } from "../../OnboardingProvider/OnboardingContext";
import { useClubSettings } from "../../settings";
import { AuthenticationContext } from "../AuthenticationContext";
import { SessionOverlayContext } from "./SessionOverlayContext";

export function SignUp({
  onSuccess,
}: {
  onSuccess: () => Promise<void> | void;
}) {
  const { isEmbed } = usePortalRoutes();
  const { api } = useGymflowModels();
  const { t } = useTranslation();
  const { sessionOverlayState } = useContext(SessionOverlayContext);
  const { mutateAsync: createAccount } = useMutationCreateAccount({
    api,
  });
  const { club } = useClubSettings();
  const PortalSignUpSchema = useMemo(
    () =>
      SignUpSchema(
        async (email) =>
          await queryClient.fetchQuery(
            apiV2.hosted.member.queries.emailExists(email),
          ),
      ),
    [],
  );
  const auth = useContext(AuthenticationContext);
  const { setCurrentScreen } = useContext(OnboardingContext);
  const formik = useFormik({
    initialValues: {
      email: sessionOverlayState.state?.email ?? "",
      firstName: "",
      lastName: "",
      mobileNumber: "",
      passwordGroup: {
        password: "",
        confirmPassword: "",
      },
      termsAndConditions: false,
    },
    validationSchema: toFormikValidationSchema(PortalSignUpSchema),
    validate: toFormikValidate(PortalSignUpSchema),
    onSubmit: async (values) => {
      const member = await createAccount({
        email: values.email,
        password: values.passwordGroup.password,
        firstName: values.firstName,
        lastName: values.lastName,
        mobileNumber: values.mobileNumber,
      });
      await auth.login(
        values.email.trim().toLowerCase(),
        values.passwordGroup.password,
      );
      if (member.onboardStatus === "INCOMPLETE") {
        setCurrentScreen("banner");
      }
      await onSuccess();
    },
  });
  return (
    <FormikProvider value={formik}>
      <div
        className={cn(
          "flex w-full max-w-lg flex-col justify-center gap-8 min-h-fit py-8",
          {
            "justify-start": isEmbed,
          },
        )}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="dark:text-gray-0 text-xl font-bold text-gray-950">
            {t("page.hostedPages.signUp.title")}
          </div>
          <div className="flex text-center font-medium text-gray-500">
            {t("page.hostedPages.signUp.description")}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <FormInput
            name="email"
            label={t("page.hostedPages.signUp.email")}
            placeholder={t("page.hostedPages.signUp.email")}
            containerClassName="w-full"
          />
          <div className="flex gap-4 max-lg:flex-col lg:flex-row">
            <FormInput
              name="firstName"
              label={t("page.hostedPages.signUp.firstName")}
              placeholder={t("page.hostedPages.signUp.firstName")}
              containerClassName="w-full lg:w-1/2"
            />
            <FormInput
              name="lastName"
              label={t("page.hostedPages.signUp.lastName")}
              placeholder={t("page.hostedPages.signUp.lastName")}
              containerClassName="w-full lg:w-1/2"
            />
          </div>
          <FormPhoneNumberInput
            name="mobileNumber"
            label={t("page.hostedPages.signUp.mobileNumber")}
            placeholder={t("page.hostedPages.signUp.mobileNumber")}
          />
          <div className="flex gap-4 max-lg:flex-col lg:flex-row">
            <FormPasswordInput
              name="passwordGroup.password"
              label={t("page.hostedPages.signUp.password")}
              placeholder={t("page.hostedPages.signUp.password")}
              containerClassName="w-full lg:w-1/2"
            />
            <FormPasswordInput
              name="passwordGroup.confirmPassword"
              label={t("page.hostedPages.signUp.confirmPassword")}
              placeholder={t("page.hostedPages.signUp.confirmPassword")}
              containerClassName="w-full lg:w-1/2"
            />
          </div>
          <div className="flex flex-row items-center justify-between gap-4">
            <FormCheckboxInput
              containerClassName="w-1/2"
              name="termsAndConditions"
              label={
                <div className="text-sm font-medium text-gray-500">
                  {t("page.hostedPages.signUp.termsAndConditions.p1")}{" "}
                  <a
                    href={club.termsConditions}
                    target="_blank"
                    rel="noreferrer"
                    className="text-secondary-600 hover:text-secondary-700 cursor-pointer text-sm font-semibold !underline hover:!no-underline"
                  >
                    {t("page.hostedPages.signUp.termsAndConditions.p2")}
                  </a>
                </div>
              }
            />
            <Button
              className="w-1/2"
              intent="secondary"
              onClick={async () => {
                await formik.submitForm();
              }}
            >
              {t("page.hostedPages.signUp.signUp")}
            </Button>
          </div>
        </div>
      </div>
    </FormikProvider>
  );
}
